import { useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import endpoints from '../../../config/endpoints';
import {
  TAPIN_CHANGEABLE_STATUS,
  TAPIN_REGISTER_TYPE,
  tapinStatusTitle,
} from '../../../enums/tapinEnum';
import restApi from '../../../services/restApi';

/**
 * Postal shipment panel for an order whose delivery method is served by Tapin.
 * The parcel is registered once - after that this only mirrors what Tapin says
 * and lets the operator push the parcel to the next status.
 */
const Tapin = ({ orderId, delivery, onChange }) => {
  const [registerType, setRegisterType] = useState(1);
  const [nextStatus, setNextStatus] = useState(2);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<any>(null);

  const isRegistered = !!delivery?.tapinOrderId;

  const fail = (text: string) =>
    Swal.fire({
      title: 'ناموفق',
      text: text || 'خطایی رخ داده',
      icon: 'error',
      confirmButtonText: 'متوجه شدم',
    });

  const register = async () => {
    setBusy(true);

    const res = await restApi(endpoints.tapin.register + orderId).post({
      registerType,
    });

    setBusy(false);

    if (res.code != 200) {
      await fail(res?.data);
      return;
    }

    onChange?.({
      ...delivery,
      tapinOrderId: res.data?.tapinOrderId,
      tapinBarcode: res.data?.barcode,
      tapinStatus: res.data?.status,
    });

    Swal.fire({
      title: 'موفق',
      text: res.data?.alreadyRegistered
        ? 'این سفارش از قبل در تاپین ثبت شده بود'
        : `مرسوله ثبت شد. بارکد: ${res.data?.barcode}`,
      icon: 'success',
      confirmButtonText: 'متوجه شدم',
    });
  };

  const track = async () => {
    setBusy(true);

    const res = await restApi(endpoints.tapin.track + orderId).get();

    setBusy(false);

    if (res.code != 200) {
      await fail(res?.data);
      return;
    }

    setDetail(res.data);

    if (res.data?.status != null && res.data.status !== delivery?.tapinStatus) {
      onChange?.({ ...delivery, tapinStatus: res.data.status });
    }
  };

  const changeStatus = async () => {
    setBusy(true);

    const res = await restApi(endpoints.tapin.changeStatus + orderId).post({
      status: nextStatus,
    });

    setBusy(false);

    if (res.code != 200) {
      await fail(res?.data);
      return;
    }

    onChange?.({ ...delivery, tapinStatus: res.data?.status });

    Swal.fire({
      title: 'موفق',
      text: `وضعیت مرسوله به «${tapinStatusTitle(res.data?.status)}» تغییر کرد`,
      icon: 'success',
      confirmButtonText: 'متوجه شدم',
    });
  };

  return (
    <div className="infoSection">
      <h1 className="dashBoardTitle">ارسال پستی (تاپین)</h1>
      <div className="userInfoContainer">
        {!isRegistered && (
          <>
            <label className="sideBarTitle">نوع ثبت</label>
            <Select
              options={TAPIN_REGISTER_TYPE}
              value={TAPIN_REGISTER_TYPE.find((e) => e.value === registerType)}
              className="width100"
              onChange={(selected) => setRegisterType(selected.value)}
            />
            <button
              className="dashboardHeader clickable"
              type="button"
              disabled={busy}
              onClick={register}
            >
              ثبت مرسوله در تاپین
            </button>
          </>
        )}

        {isRegistered && (
          <>
            <label className="sideBarTitle">بارکد مرسوله</label>
            <input
              className="editProductInput"
              readOnly
              value={delivery?.tapinBarcode || ''}
            />
            <label className="sideBarTitle">شناسه سفارش تاپین</label>
            <input
              className="editProductInput"
              readOnly
              value={delivery?.tapinOrderId || ''}
            />
            <label className="sideBarTitle">وضعیت</label>
            <input
              className="editProductInput"
              readOnly
              value={tapinStatusTitle(delivery?.tapinStatus)}
            />
            <label className="sideBarTitle">تغییر وضعیت</label>
            <Select
              options={TAPIN_CHANGEABLE_STATUS}
              value={TAPIN_CHANGEABLE_STATUS.find((e) => e.value === nextStatus)}
              className="width100"
              onChange={(selected) => setNextStatus(selected.value)}
            />
            <button
              className="dashboardHeader clickable"
              type="button"
              disabled={busy}
              onClick={changeStatus}
            >
              اعمال تغییر وضعیت
            </button>
            <button
              className="dashboardHeader clickable"
              type="button"
              disabled={busy}
              onClick={track}
            >
              استعلام از تاپین
            </button>
            {detail && (
              <>
                <label className="sideBarTitle">وزن اعلامی</label>
                <input
                  className="editProductInput"
                  readOnly
                  value={detail?.total_weight ?? detail?.weight ?? '—'}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Tapin;
